import { Word } from '@/player-config/src/types/transcript-types';
import { SpeakerWithDetails } from '@/player-config/src/types/speaker-types';
import { ClipPlayerStore } from '@/stores/playerV2';

export interface SpeakerRowDetails {
  speaker_label: string;
  speaker: SpeakerWithDetails;
  start: string;
  end: string;
}

export interface VideoSegmentDetails {
  id: string;
  name: string;
  fromFrame: number;
  toFrame: number;
  duration: number;
}

export interface TimelineDeleteChangeDuringDrag {
  deletedId: number;
  compositeSegmentId: string;
  type: 'add' | 'remove' | 'reset';
  startTime: number;
  endTime: number;
}

export enum TranscriptPauseType {
  MicroPause = 'microPause',
  LongPause = 'longPause',
  DeadSpace = 'deadSpace'
}

export type SpeakerPace = 'slow' | 'normal' | 'fast';

export type PauseContext = {
  isParagraphBoundary: boolean;
  hasSentenceEndingPunctuation: boolean;
  hasClauseEndingPunctuation: boolean;
};

export interface TranscriptPauseEntry {
  segmentId: string;
  afterWordIndex: number;
  startTime: number;
  endTime: number;
  pauseDuration: number;
  pauseType: TranscriptPauseType;
  recommendedPause: number;
  label: string;
  isParagraphBoundary: boolean;
}

export interface TranscriptParagraph {
  segmentId: string;
  segmentStartTime?: number;
  speakerDetails?: SpeakerRowDetails;
  chapterDetails?: { id: string; title: string; start: number; end?: number };
  hasSomeDeletedBefore?: boolean;
  hasSomeDeletedAfter?: boolean;
  videoSegmentDetails?: VideoSegmentDetails;
  words: Word[];
  pauseEntries?: TranscriptPauseEntry[];
  inlinePausesByWordIndex?: Map<number, TranscriptPauseEntry>;
  deadSpacePauseEntries?: TranscriptPauseEntry[];
}

export interface TranscriptSelection {
  words: Record<string, Word[]>;
  duration: number | string;
  isSingleParagraphSelection: boolean;
  selectionStartTime: number;
  selectionEndTime: number;
  startSegmentId: string | null;
  endSegmentId: string | null;
}

export interface TranscriptWordBounds {
  startIndex: number;
  endIndex: number;
}

export interface SearchParagraphWord {
  content: string;
  startIndex: number; // start inside paragraph
  endIndex: number; // end inside paragraph
  start_time: number;
  end_time: number;
  index: number;
}

export interface SearchParagraph {
  content: string;
  words: SearchParagraphWord[];
  index: number;
  segmentId: string;
}

export interface SearchResult {
  paragraph: string;
  index: number;
  segmentId: string;
  start: number; // start of result in paragraph
  end: number; // end of result
  words: SearchParagraphWord[];
}

export interface HighlightedSearchParagraph {
  index: number;
  words: { [key: number]: HighlightedSearchWord };
}

export interface HighlightedSearchWord extends SearchParagraphWord {
  highlightStart: number;
  highlightEnd: number;
  highlightSpace: boolean;
}

export interface ClipTranscriptWordProps {
  word: Word;
  index: number;
  highlightStartTime: number;
  highlightEndTime: number;
  playerStore: ClipPlayerStore;
  selectedWords?: Word[];
  foundWords?: { [key: number]: HighlightedSearchWord };
  offset?: number;
}

export interface TranscriptSelectionRange {
  start: number;
  end: number;
}

export interface TranscriptSelectionRanges {
  deleted: TranscriptSelectionRange[];
  active: TranscriptSelectionRange[];
}

export interface TranscriptExtensionStart {
  startTime: number;
  startSegmentId: string;
}

export interface TranscriptExtensionEnd {
  endTime: number;
  endSegmentId: string;
}

export type TranscriptExtension = TranscriptExtensionStart | TranscriptExtensionEnd;
